import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ShieldCheck, Loader2, Eye, EyeOff } from 'lucide-react';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import LegalFooter from '../components/LegalFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * ResetPasswordPage — Phase 27.6 (P0.1)
 *
 * Public page. Reads `?token=...` from the URL (sent in the reset email
 * link). User types a new password (twice); on success we show a
 * confirmation + link to /login.
 */
const ResetPasswordPage = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = new URLSearchParams(location.search).get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast({
        title: t('reset_password.missing_token_title'),
        description: t('reset_password.missing_token_desc'),
        variant: 'destructive',
      });
      return;
    }
    if (pwd.length < 6) {
      toast({
        title: t('reset_password.short_password_title'),
        description: t('reset_password.short_password_desc'),
        variant: 'destructive',
      });
      return;
    }
    if (pwd !== confirm) {
      toast({ title: t('reset_password.mismatch_title'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setDone(true);
      toast({
        title: t('reset_password.done_toast_title'),
        description: t('reset_password.done_toast_desc'),
      });
      // Auto-redirect to login after 2s.
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      toast({
        title: t('reset_password.error_title'),
        description: err.response?.data?.detail || t('reset_password.error_default'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="mb-4 flex w-full max-w-md justify-end">
        <LanguageSwitcher variant="full" align="end" testId="reset-language-switcher" />
      </div>
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="reset-password-card">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
            {done
              ? <ShieldCheck className="h-7 w-7 text-emerald-600" strokeWidth={1.8} />
              : <Lock className="h-7 w-7 text-amber-600" strokeWidth={1.8} />}
          </div>
          {done ? (
            <div className="text-center" data-testid="reset-password-success">
              <h1 className="text-2xl font-bold">{t('reset_password.done_title')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t('reset_password.done_desc')}</p>
              <Link
                to="/login"
                className="mt-4 inline-block text-sm font-semibold gold-text hover:underline"
                data-testid="reset-password-login-link"
              >
                {t('reset_password.go_to_login')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-center text-2xl font-bold text-foreground">{t('reset_password.title')}</h1>
              <p className="mt-2 text-center text-sm text-muted-foreground">
                {t('reset_password.subtitle')}
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">{t('reset_password.new_password_label')}</label>
                  <div className="relative">
                    <Input
                      type={showPwd ? 'text' : 'password'}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      autoComplete="new-password"
                      placeholder={t('reset_password.placeholder_pwd')}
                      className="h-10 bg-background pr-10"
                      data-testid="reset-password-pwd-input"
                    />
                    {pwd && (
                      <button
                        type="button"
                        onClick={() => setShowPwd((p) => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground"
                        aria-label={t('reset_password.show_password_aria')}
                      >
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">{t('reset_password.confirm_password_label')}</label>
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    placeholder={t('reset_password.placeholder_pwd')}
                    className="h-10 bg-background"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !pwd || !confirm}
                  className="gold-gradient h-11 w-full text-base font-semibold"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                  {loading ? t('reset_password.submitting') : t('reset_password.submit')}
                </Button>
              </form>
            </>
          )}
        </div>

        {!done && (
          <div className="mt-4 text-center text-xs">
            <Link to="/forgot-password" className="font-semibold text-muted-foreground hover:underline">
              {t('reset_password.request_new_link')}
            </Link>
            <span className="mx-2 text-muted-foreground">·</span>
            <Link to="/login" className="font-semibold text-muted-foreground hover:underline">
              {t('reset_password.back_to_login')}
            </Link>
          </div>
        )}
      </div>
      <LegalFooter className="mt-8 max-w-md px-4" />
    </div>
  );
};

export default ResetPasswordPage;
